import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Form, Input, Button, DatePicker, Upload, Space, Typography, Row, Col,
  message, Spin, Modal
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, PictureOutlined, AudioOutlined,
  VideoCameraOutlined, DeleteOutlined, UploadOutlined, BookOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { articlesAPI } from '../../utils/api';
import { uploadImage, uploadAudio, uploadVideo, generateVideoThumbnail } from '../../utils/caminoUploads';
import CaminoRichEditor from '../../components/CaminoRichEditor';
import './CaminoEditor.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SECTION_MOCHILA = 'Camino — En mi mochila';

// Editor de material de referencia ("En mi mochila"): oración del peregrino,
// qué llevar, horarios, etc. Sin lógica de programación — siempre 'published'.
// La fecha se usa solo para controlar el orden en el listado (DESC).
const MochilaEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const loadEntry = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await articlesAPI.getArticleById(id);
      if (!res.success) throw new Error(res.error || 'No se pudo cargar la entrada');
      const a = res.data;
      if (a.section !== SECTION_MOCHILA) {
        message.warning('Esta entrada no pertenece a "En mi mochila".');
        navigate('/camino');
        return;
      }
      form.setFieldsValue({
        title: a.title || '',
        subtitle: a.subtitle || '',
        content: a.content || '',
        date: a.date ? dayjs(a.date) : dayjs()
      });
      setImageUrl(a.imageUrl || null);
      setAudioUrl(a.audioUrl || null);
      setVideoUrl(a.videoUrl || null);
      setVideoThumbnail(a.videoThumbnail || null);
    } catch (error) {
      console.error('Error cargando entrada:', error);
      message.error('Error al cargar la entrada');
      navigate('/camino');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, form, navigate]);

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ date: dayjs() });
    } else {
      loadEntry();
    }
  }, [isNew, loadEntry, form]);

  const beforeImage = (file) => {
    if (!file.type.startsWith('image/')) {
      message.error('Solo se permiten imágenes');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error('La imagen excede 10 MB');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleImageUpload = async ({ file }) => {
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      setImageUrl(url);
      message.success('Imagen subida');
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error || 'Error subiendo imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const beforeAudio = (file) => {
    if (!file.type.startsWith('audio/')) {
      message.error('Solo se permiten audios');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleAudioUpload = async ({ file }) => {
    setUploadingAudio(true);
    try {
      const url = await uploadAudio(file);
      setAudioUrl(url);
      message.success('Audio subido');
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error || 'Error subiendo audio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const beforeVideo = (file) => {
    if (!file.type.startsWith('video/')) {
      message.error('Solo se permiten videos');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleVideoUpload = async ({ file }) => {
    setUploadingVideo(true);
    try {
      const url = await uploadVideo(file);
      setVideoUrl(url);
      message.success('Video subido');

      try {
        message.loading({ content: 'Generando thumbnail…', key: 'thumb', duration: 0 });
        const thumbFile = await generateVideoThumbnail(file);
        const thumbUrl = await uploadImage(thumbFile);
        setVideoThumbnail(thumbUrl);
        message.success({ content: 'Thumbnail generado', key: 'thumb' });
      } catch (thumbErr) {
        console.warn('No se pudo generar thumbnail:', thumbErr);
        message.info({ content: 'Video subido (sin thumbnail automático)', key: 'thumb' });
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.error || 'Error subiendo video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const dateIso = values.date ? values.date.toISOString() : new Date().toISOString();
      const payload = {
        title: values.title.trim(),
        subtitle: (values.subtitle || '').trim() || null,
        content: values.content || '',
        section: SECTION_MOCHILA,
        date: dateIso,
        status: 'published',
        imageUrl: imageUrl || null,
        hasAudio: !!audioUrl,
        audioUrl: audioUrl || null,
        hasVideo: !!videoUrl,
        videoUrl: videoUrl || null,
        videoType: videoUrl ? 'mp4' : null,
        videoThumbnail: videoThumbnail || null
      };

      const res = isNew
        ? await articlesAPI.createArticle(payload)
        : await articlesAPI.updateArticle(id, payload);

      if (!res.success) throw new Error(res.error || 'Error al guardar');

      message.success('Entrada guardada');
      navigate('/camino');
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error guardando:', error);
      message.error(error?.response?.data?.error || 'Error al guardar la entrada');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  return (
    <div className="camino-editor" style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/camino')}>
              Volver
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              <BookOutlined /> {isNew ? 'Nuevo material' : 'Editar material'}
            </Title>
          </Space>
          <div>
            <Text type="secondary">Sección: {SECTION_MOCHILA}</Text>
          </div>
        </Col>
      </Row>

      <Form form={form} layout="vertical">
        <Card title="Datos" style={{ marginBottom: 16 }}>
          <Form.Item
            name="title"
            label="Título"
            rules={[
              { required: true, message: 'El título es obligatorio' },
              { min: 3, message: 'Mínimo 3 caracteres' }
            ]}
          >
            <Input placeholder="Ej.: Oración del peregrino, Qué llevar en la mochila…" maxLength={200} showCount />
          </Form.Item>

          <Form.Item name="subtitle" label="Subtítulo (opcional)">
            <TextArea rows={2} placeholder="Descripción breve o resumen" maxLength={300} showCount />
          </Form.Item>

          <Form.Item
            name="date"
            label="Fecha (para ordenar en el listado)"
            rules={[{ required: true, message: 'Indica la fecha' }]}
            extra="No es una fecha de publicación programada. Se usa solo para ordenar: las más recientes aparecen arriba."
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              style={{ width: '100%' }}
              placeholder="Selecciona fecha"
            />
          </Form.Item>
        </Card>

        <Card title="Multimedia" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text strong><PictureOutlined /> Imagen destacada</Text>
              </div>
              {imageUrl ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <img src={imageUrl} alt="destacada"
                    style={{ width: '100%', borderRadius: 8, maxHeight: 240, objectFit: 'cover' }} />
                  <Button danger size="small" icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() => setImageUrl(null)}>Quitar</Button>
                </div>
              ) : null}
              <Upload
                customRequest={handleImageUpload}
                beforeUpload={beforeImage}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} loading={uploadingImage}>
                  {imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
                </Button>
              </Upload>
            </Col>

            <Col xs={24} md={12}>
              <div style={{ marginBottom: 8 }}>
                <Text strong><AudioOutlined /> Audio (opcional)</Text>
              </div>
              {audioUrl ? (
                <div style={{ marginBottom: 8 }}>
                  <audio src={audioUrl} controls style={{ width: '100%' }} />
                  <Button danger size="small" icon={<DeleteOutlined />}
                    style={{ marginTop: 4 }} onClick={() => setAudioUrl(null)}>
                    Quitar audio
                  </Button>
                </div>
              ) : null}
              <Upload
                customRequest={handleAudioUpload}
                beforeUpload={beforeAudio}
                showUploadList={false}
                accept="audio/*"
              >
                <Button icon={<UploadOutlined />} loading={uploadingAudio}>
                  {audioUrl ? 'Cambiar audio' : 'Subir audio'}
                </Button>
              </Upload>
            </Col>

            <Col xs={24}>
              <div style={{ marginBottom: 8 }}>
                <Text strong><VideoCameraOutlined /> Video (opcional)</Text>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  Se genera thumbnail automático a partir del primer frame útil.
                </Text>
              </div>
              {videoUrl ? (
                <div style={{ marginBottom: 8 }}>
                  <video src={videoUrl} controls style={{ width: '100%', maxHeight: 340, background: '#000' }} />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {videoThumbnail && (
                      <img src={videoThumbnail} alt="thumbnail"
                        style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                    )}
                    <Button danger size="small" icon={<DeleteOutlined />}
                      onClick={() => { setVideoUrl(null); setVideoThumbnail(null); }}>
                      Quitar video
                    </Button>
                  </div>
                </div>
              ) : null}
              <Upload
                customRequest={handleVideoUpload}
                beforeUpload={beforeVideo}
                showUploadList={false}
                accept="video/*"
              >
                <Button icon={<UploadOutlined />} loading={uploadingVideo}>
                  {videoUrl ? 'Cambiar video' : 'Subir video'}
                </Button>
              </Upload>
            </Col>
          </Row>
        </Card>

        <Card title="Contenido" style={{ marginBottom: 16 }}>
          <Form.Item name="content" valuePropName="value">
            <CaminoRichEditor placeholder="Texto principal — oración, listado de material, horarios…" />
          </Form.Item>
        </Card>

        <Card>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={saving}
            >
              Guardar
            </Button>
            <Button onClick={() => {
              Modal.confirm({
                title: '¿Descartar cambios?',
                content: 'Volverás al listado sin guardar.',
                okText: 'Descartar', cancelText: 'Seguir editando',
                okButtonProps: { danger: true },
                onOk: () => navigate('/camino')
              });
            }}>Cancelar</Button>
          </Space>
        </Card>
      </Form>
    </div>
  );
};

export default MochilaEditor;
